import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import type { ColumnProps } from "@cofano/cck";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";
import SalesCharterStatusSelect, { type SalesCharterStatusSelectOption } from "view/charters/components/selects/SalesCharterStatusSelect";
import InvoiceGroupTypeSelect, { type InvoiceGroupTypeSelectOption } from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import RelationSelect, { type RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import ShipSelect, { type ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusSelect, { type StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import StatusChip from "view/statuses/components/changer/StatusChip";
import useDeclarationFilters from "view/declarations/utils/useDeclarationFilters";
import { DataFormat } from "imports/Utils";
import { DossierLink, InvoiceLink, RelationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { CharterSubtype, CharterType, StatusType } from "__generated__/enums";
import type { DeclarationRow } from "view/declarations/components/table/DeclarationsTable";

const useDeclarationTable = (charterType: CharterType | null) => {

	const session = useStoreState(store => store.session);
	const { filterFields } = useDeclarationFilters(charterType);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getSearchInput = (filter: Record<string, any>, forceInvoiced: boolean | undefined, forceCharterType: CharterType | null) => ({
		charterNumber: filter.charterNumber,
		customerReference: filter.customerReference,
		dossierNumber: filter.dossierNumber,
		salesCharterNumber: filter.salesCharter,
		purchaseCharterNumber: filter.purchaseCharter,
		shipIds: filter.ship?.map((o: ShipSelectOption) => o.id),
		creditors: filter.creditor?.map((o: RelationSelectOption) => o.id),
		debtors: filter.debtor?.map((o: RelationSelectOption) => o.id),
		statusIds: filter.declarationStatus?.map((s: StatusSelectOption) => s.id),
		salesCharterStatuses: filter.salesCharterStatus?.map((s: SalesCharterStatusSelectOption) => s.name),
		charterTypes: forceCharterType ? [ forceCharterType ] : (filter.charterType ? [ filter.charterType as CharterType ] : undefined),
		charterSubtypes: filter.charterSubtype as CharterSubtype[] | undefined,
		blDate: filter.blDate ? { from: filter.blDate[0], to: filter.blDate[1] } : undefined,
		window: filter.window ? { from: filter.window[0], to: filter.window[1] } : undefined,
		invoiced: forceInvoiced !== undefined
			? forceInvoiced
			: (typeof filter.invoiced === "boolean" ? filter.invoiced : undefined),
		invoiceGroupTypeIds: filter.invoiceGroupType?.map((o: InvoiceGroupTypeSelectOption) => o.id),
	});

	const charterTypeColumn: ColumnProps = {
		id: "charterType",
		title: intl.formatMessage({ id: "TYPE" }),
		render: (row: DeclarationRow) => row.charter ? <CharterTypeTag type={row.charter.type} /> : null,
		filterComponent: (value, onChange) => (
			<CharterTypeSelect
				label={intl.formatMessage({ id: "TYPE" })}
				value={value}
				onChange={onChange}
				disableSearch />
		),
	};

	type CharterRef = { id: number; charterNumber: string; type: CharterType; dossierNumber?: string | null; customerReference?: string | null };
	type CharterExtra = CharterRef & {
		ships?: ({ id: number; name: string } | null)[] | null;
		voyages?: ({ ship?: { id: number; name: string } | null; cargoCharters?: (CharterRef | null)[] | null } | null)[] | null;
		customerReference?: string | null;
		dossierNumber?: string | null;
		purchaseCharter?: CharterExtra | null;
	};
	const charterExtra = (row: DeclarationRow) => row.charter as unknown as CharterExtra | null | undefined;

	const shipColumn: ColumnProps = {
		id: "ship",
		title: intl.formatMessage({ id: "SHIP" }),
		render: (row: DeclarationRow) => {
			const c = charterExtra(row);
			const charter = row.charter?.type === CharterType.Purchase ? c : c?.purchaseCharter;
			const ship = charter?.voyages?.[0]?.ship ?? charter?.ships?.[0] ?? null;
			return ship ? <ShipLink ship={ship} /> : null;
		},
		filterComponent: (value, onChange) => (
			<ShipSelect label={intl.formatMessage({ id: "SHIP" })}
				value={value}
				onChange={onChange}
				multiple />
		),
	};

	const customerReferenceColumn: ColumnProps = {
		id: "customerReference",
		title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
		render: (row: DeclarationRow) => {
			const c = charterExtra(row);
			if (row.charter?.type === CharterType.Purchase) {
				const customerReferences = (c?.voyages ?? [])
					.flatMap(v => v?.cargoCharters ?? [])
					.map(sc => sc?.customerReference);
				const unique = Array.from(new Set(customerReferences));
				return unique.length ? unique.join(", ") : null;
			}
			return c?.customerReference ?? null;
		},
		filterComponent: "TEXT",
	};

	const dossierNumberColumn: ColumnProps = {
		id: "dossierNumber",
		title: intl.formatMessage({ id: "DOSSIER_NUMBER" }),
		render: (row: DeclarationRow) => {
			const c = charterExtra(row);
			if (c?.dossierNumber) {
				return c.dossierNumber;
			}
			const dossierNumbers = (c?.voyages ?? [])
				.flatMap(v => v?.cargoCharters ?? [])
				.map(sc => sc?.dossierNumber);
			const unique = Array.from(new Set(dossierNumbers));
			return unique.length ? unique.join(", ") : null;
		},
		filterComponent: "TEXT",
	};

	const salesCharterColumn: ColumnProps = {
		id: "salesCharter",
		title: intl.formatMessage({ id: "SALES_CHARTER" }),
		filterComponent: "TEXT",
		render: (row: DeclarationRow) => {
			const salesCharters = (charterExtra(row)?.voyages ?? [])
				.flatMap(v => v?.cargoCharters ?? [])
				.filter((c): c is CharterRef => c != null);
			const unique = Array.from(new Map(salesCharters.map(c => [ c.id, c ])).values());
			return unique.length
				? <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 8 }}>
					{unique.map(c => <DossierLink key={c.id} charter={c} />)}
				</span>
				: null;
		},
	};

	const purchaseCharterColumn: ColumnProps = {
		id: "purchaseCharter",
		title: intl.formatMessage({ id: "PURCHASE_CHARTER" }),
		filterComponent: "TEXT",
		render: (row: DeclarationRow) => {
			const purchaseCharter = charterExtra(row)?.purchaseCharter;
			return purchaseCharter ? <DossierLink charter={purchaseCharter} /> : null;
		},
	};

	const salesCharterStatusColumn: ColumnProps = {
		id: "salesCharterStatus",
		title: `${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "STATUS" }).toLowerCase()}`,
		render: (row: DeclarationRow) => row.charter && "status" in row.charter ? (
			<StatusChip
				label={intl.formatMessage({ id: `${row.charter.status.languageKeyPrefix}${row.charter.status.name}` })}
				color={row.charter.status.color} />
		) : null,
		filterComponent: (value, onChange) => (
			<SalesCharterStatusSelect
				label={`${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "STATUS" }).toLowerCase()}`}
				value={value}
				onChange={onChange}
				multiple />
		),
	};

	const getColumns = (refetch: () => void, hideInvoiced: boolean, hideCharterType?: boolean, charterSource?: boolean, showSalesCharter?: boolean, showPurchaseCharter?: boolean, hideCharterStatus?: boolean): ColumnProps[] => [ {
		id: "charterNumber",
		title: intl.formatMessage({ id: "CHARTER" }),
		render: (row: DeclarationRow) => <DossierLink charter={row.charter} />,
		filterComponent: "TEXT",
	}, ...(showSalesCharter ? [ salesCharterColumn ] : []), ...(showPurchaseCharter ? [ purchaseCharterColumn ] : []), ...(charterSource ? [ shipColumn, customerReferenceColumn, dossierNumberColumn ] : []), {
		id: "blDate",
		title: intl.formatMessage({ id: "BL_DATE" }),
		render: (row: DeclarationRow) => row.charter && "blDate" in row.charter && row.charter.blDate
			? <FormattedDate date={row.charter.blDate} format="ll" />
			: null,
		filterComponent: "DATE_RANGE",
	}, {
		id: "window",
		title: intl.formatMessage({ id: "WINDOW" }),
		render: (row: DeclarationRow) => row.window && (row.window.from || row.window.to)
			? <FormattedDateWindow from={row.window.from} to={row.window.to} format="ll" />
			: null,
		filterComponent: "DATE_RANGE",
	}, ...(hideCharterType ? [] : [ charterTypeColumn ]), {
		id: "charterSubtype",
		title: intl.formatMessage({ id: "CHARTER_TYPE" }),
		render: (row: DeclarationRow) => row.charter ? <CharterSubtypeTag type={row.charter.subtype} /> : null,
		filterComponent: (value, onChange) => (
			<CharterSubtypeSelect
				label={intl.formatMessage({ id: "CHARTER_TYPE" })}
				value={value}
				onChange={onChange}
				customCharterSubtypes={Object.values(CharterSubtype)}
				multiple />
		),
	}, ...(hideCharterStatus ? [] : [ salesCharterStatusColumn ]), {
		id: "creditor",
		title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
		render: (row: DeclarationRow) => <RelationLink relation={row.invoiceLines?.[0]?.creditor} />,
		filterComponent: (value, onChange) => (
			<RelationSelect
				label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
				value={value}
				onChange={onChange}
				multiple />
		),
	}, {
		id: "debtor",
		title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
		render: (row: DeclarationRow) => <RelationLink relation={row.invoiceLines?.[0]?.debtor} />,
		filterComponent: (value, onChange) => (
			<RelationSelect
				label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
				value={value}
				onChange={onChange}
				multiple />
		),
	}, {
		id: "invoiceGroupType",
		title: intl.formatMessage({ id: "INVOICE_GROUP_TYPE" }),
		render: (row: DeclarationRow) => row.invoiceGroupType?.name ?? null,
		filterComponent: (value, onChange) => (
			<InvoiceGroupTypeSelect
				label={intl.formatMessage({ id: "INVOICE_GROUP_TYPE" })}
				value={value}
				onChange={onChange}
				charterType={charterType}
				includeAdvancePayment
				includeParentContractExclusive
				multiple
			/>
		),
	}, {
		id: "lineCount",
		title: intl.formatMessage({ id: "INVOICE_LINES" }),
		render: (row: DeclarationRow) => row.invoiceLines?.length ?? 0,
	}, {
		id: "totalPriceExcVAT",
		title: intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" }),
		render: (row: DeclarationRow) => DataFormat.price(row.totalPriceExcVAT ?? 0),
	}, {
		id: "declarationStatus",
		title: intl.formatMessage({ id: "DECLARATION_STATUS" }),
		render: (row: DeclarationRow) => {
			if (row.lastStatusChange) {
				return <StatusChanger
					statusType={StatusType.Declaration}
					entity={row}
					refetch={() => refetch()}
					showHistory />;
			}
		},
		filterComponent: (value, onChange) => (
			<StatusSelect
				label={intl.formatMessage({ id: "DECLARATION_STATUS" })}
				statuses={session.statuses?.DECLARATION ?? []}
				value={value}
				onChange={onChange}
				multiple />
		),
	}, ...(hideInvoiced ? [] : [ {
		id: "invoiced",
		title: intl.formatMessage({ id: "INVOICE" }),
		render: (row: DeclarationRow) => {
			const lines = (row.invoiceLines ?? []).filter((l): l is NonNullable<typeof l> => l != null);
			const invoice = lines.find(l => l.invoice != null)?.invoice;
			return invoice ? <InvoiceLink invoice={invoice} /> : null;
		},
		filterComponent: "BOOLEAN" as const,
	} ]) ];

	return { filterFields, getSearchInput, getColumns };
};

export default useDeclarationTable;
